import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import GscPanel from './GscPanel';

const SITEMAP_URL = 'https://ixduinqmfepnggmheoci.supabase.co/functions/v1/sitemap-xml';
const SITE_URL = 'https://teknonovahub.com';

type SitemapStats = {
  total: number;
  news: number;
  categories: number;
  static: number;
};

type ActiveTab = 'sitemap' | 'gsc';

export default function SitemapTab() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('sitemap');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<SitemapStats | null>(null);
  const [copiedStep, setCopiedStep] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(key);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { count: newsCount } = await supabase
        .from('news')
        .select('*', { count: 'exact', head: true });
      const { count: catCount } = await supabase
        .from('categories')
        .select('*', { count: 'exact', head: true });

      const staticPages = 6;
      const total = (newsCount || 0) + (catCount || 0) + staticPages;
      setStats({
        total,
        news: newsCount || 0,
        categories: catCount || 0,
        static: staticPages,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    if (preview) { setPreview(false); return; }
    setLoading(true);
    try {
      const res = await fetch(SITEMAP_URL);
      const text = await res.text();
      setPreviewContent(text.slice(0, 3000) + (text.length > 3000 ? '\n...(devamı var)' : ''));
      setPreview(true);
    } catch {
      setPreviewContent('Önizleme yüklenirken hata oluştu.');
      setPreview(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadSitemap = async () => {
    setLoading(true);
    try {
      const res = await fetch(SITEMAP_URL);
      const text = await res.text();
      const blob = new Blob([text], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sitemap.xml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // ignore download error
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      key: 'step1',
      num: '1',
      title: "Google Search Console'a Giriş Yapın",
      desc: 'search.google.com/search-console adresine gidin ve sitenizi seçin.',
      action: null as (() => void) | null,
      copyText: undefined as string | undefined,
      link: 'https://search.google.com/search-console',
      linkLabel: "Search Console'u Aç",
    },
    {
      key: 'step2',
      num: '2',
      title: 'Sitemap Bölümüne Gidin',
      desc: 'Sol menüden "Dizin Oluşturma" → "Site Haritaları" bölümünü seçin.',
      action: null as (() => void) | null,
      copyText: undefined as string | undefined,
      link: null as string | null,
      linkLabel: null as string | null,
    },
    {
      key: 'step3',
      num: '3',
      title: "Sitemap URL'ini Yapıştırın",
      desc: '"Yeni site haritası ekle" kutusuna aşağıdaki URL\'yi yapıştırın ve "Gönder" butonuna tıklayın.',
      action: (() => copyToClipboard(SITEMAP_URL, 'step3')) as (() => void) | null,
      copyText: SITEMAP_URL,
      link: null as string | null,
      linkLabel: null as string | null,
    },
    {
      key: 'step4',
      num: '4',
      title: 'Doğrulama Bekleyin',
      desc: "Google birkaç saat içinde sitemap'inizi işleyecek ve sayfalarınızı dizine ekleyecek. Durum \"Başarılı\" olarak görünmeli.",
      action: null as (() => void) | null,
      copyText: undefined as string | undefined,
      link: null as string | null,
      linkLabel: null as string | null,
    },
  ];

  return (
    <div className="flex flex-col gap-6">

      {/* Tab switcher */}
      <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit">
        {([
          { key: 'sitemap' as ActiveTab, label: 'Sitemap', icon: 'ri-map-2-line' },
          { key: 'gsc' as ActiveTab, label: 'Google Search Console', icon: 'ri-search-eye-line' },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === t.key ? 'bg-[#0A0E27] text-white' : 'text-gray-500 hover:text-[#0A0E27]'
            }`}
          >
            <i className={t.icon} />{t.label}
          </button>
        ))}
      </div>

      {/* ─── GSC TAB ─── */}
      {activeTab === 'gsc' && <GscPanel />}

      {/* ─── SITEMAP TAB ─── */}
      {activeTab === 'sitemap' && (
        <div className="flex flex-col gap-6">

          {/* Hero Banner */}
          <div className="bg-gradient-to-r from-[#0A0E27] to-[#1a2040] rounded-xl p-5 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-2-line text-[#00FF88] text-lg" />
                  </div>
                  <h2 className="font-black text-base">Sitemap.xml Aktif</h2>
                </div>
                <p className="text-xs text-white/50">
                  Haberleriniz otomatik olarak Google&apos;a bildirilir. Site haritanız her gün güncellenir.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={fetchPreview}
                  disabled={loading}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-eye-line'} />
                  {preview ? 'Kapat' : 'Önizle'}
                </button>
                <button
                  onClick={downloadSitemap}
                  disabled={loading}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} />
                  İndir
                </button>
                <a
                  href={SITEMAP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 bg-[#00FF88] text-[#0A0E27] text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#00FF88]/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line" />Sitemap&apos;i Aç
                </a>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#0A0E27] text-sm">Sitemap İçeriği</h3>
              <button
                onClick={fetchStats}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#0A0E27] cursor-pointer whitespace-nowrap"
              >
                <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} />
                Sayıları Güncelle
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Toplam URL', value: stats ? stats.total : '—', icon: 'ri-links-line', color: 'text-[#0A0E27]', bg: 'bg-gray-50' },
                { label: 'Haber Sayfası', value: stats ? stats.news : '—', icon: 'ri-newspaper-line', color: 'text-green-600', bg: 'bg-green-50' },
                { label: 'Kategori Sayfası', value: stats ? stats.categories : '—', icon: 'ri-folder-line', color: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'Statik Sayfa', value: stats ? stats.static : '—', icon: 'ri-pages-line', color: 'text-rose-600', bg: 'bg-rose-50' },
              ].map(s => (
                <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-white ${s.color}`}>
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <div>
                    <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-gray-400">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="bg-[#0A0E27] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider">Sitemap Önizleme (XML)</p>
                <button onClick={() => setPreview(false)} className="w-5 h-5 flex items-center justify-center text-white/40 hover:text-white cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </div>
              <pre className="text-xs text-[#00FF88] overflow-x-auto max-h-64 leading-relaxed whitespace-pre-wrap break-all">{previewContent}</pre>
            </div>
          )}

          {/* Sitemap URL */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="font-bold text-[#0A0E27] text-sm mb-3 flex items-center gap-2">
              <i className="ri-links-line" />Sitemap URL
            </h3>
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
              <code className="flex-1 text-xs text-gray-600 break-all">{SITEMAP_URL}</code>
              <button
                onClick={() => copyToClipboard(SITEMAP_URL, 'url')}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <i className={copiedStep === 'url' ? 'ri-check-line text-green-500' : 'ri-clipboard-line text-gray-500'} />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              <i className="ri-information-line mr-1" />
              Bu URL her zaman güncel verileri döndürür. Google bu URL&apos;yi otomatik olarak günlük okur.
            </p>
          </div>

          {/* Google Search Console Steps */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="font-bold text-[#0A0E27] text-sm mb-4 flex items-center gap-2">
              <i className="ri-google-line" />
              Google Search Console&apos;a Sitemap Ekleme
            </h3>
            <div className="flex flex-col gap-3">
              {steps.map((step) => (
                <div key={step.key} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A0E27] text-white text-xs font-black flex-shrink-0">
                      {step.num}
                    </div>
                    {step.key !== 'step4' && <div className="w-px flex-1 bg-gray-200 mt-1 mb-1" />}
                  </div>
                  <div className="flex-1 pb-3">
                    <h4 className="text-sm font-semibold text-[#0A0E27] mb-1">{step.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed mb-2">{step.desc}</p>
                    {step.action && (
                      <button
                        onClick={step.action}
                        className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 hover:border-[#0A0E27] hover:text-[#0A0E27] transition-colors cursor-pointer max-w-full"
                      >
                        <code className="flex-1 truncate text-left">{step.copyText}</code>
                        <span className="flex-shrink-0 flex items-center gap-1 font-semibold">
                          {copiedStep === step.key ? (
                            <><i className="ri-check-line text-green-500" />Kopyalandı!</>
                          ) : (
                            <><i className="ri-clipboard-line" />Kopyala</>
                          )}
                        </span>
                      </button>
                    )}
                    {step.link && (
                      <a
                        href={step.link}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A0E27] bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line" />{step.linkLabel}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* robots.txt Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-robot-line text-amber-600 text-lg" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">robots.txt Hakkında</p>
                <p className="text-xs text-amber-700 leading-relaxed mb-2">
                  Siteniz yayında olduğunda <strong>{SITE_URL}/robots.txt</strong> adresinde aşağıdaki içeriği barındırmanız önerilir.
                </p>
                <div className="bg-amber-100 rounded-lg p-2.5 flex items-start gap-2">
                  <pre className="text-xs text-amber-900 flex-1 leading-relaxed">{`User-agent: *\nAllow: /\n\nSitemap: ${SITEMAP_URL}`}</pre>
                  <button
                    onClick={() => copyToClipboard(`User-agent: *\nAllow: /\n\nSitemap: ${SITEMAP_URL}`, 'robots')}
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-amber-200 rounded-lg hover:bg-amber-300 cursor-pointer transition-colors"
                  >
                    <i className={copiedStep === 'robots' ? 'ri-check-line text-green-700 text-xs' : 'ri-clipboard-line text-amber-800 text-xs'} />
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
